import React from 'react';
import { useCustomNavigate } from '../hooks/useCustomNavigate';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { 
  Target, 
  BookOpen, 
  Calendar,
  Instagram,
  AlertCircle,
  Users
} from 'lucide-react';
import { NavigationMenu } from '../components/NavigationMenu';
import { PageHeader } from '../components/PageHeader';
import { StudioOnboarding, useStudioOnboarding } from '../components/StudioOnboarding';
import { useState } from 'react';

type Feature = {
  id: string;
  title: string;
  icon: React.ReactNode;
  description: string;
  path: string;
  isPrimary?: boolean;
  inDevelopment?: boolean;
};

function Home() {
  useDocumentTitle('Главная');
  const navigate = useCustomNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(true);
  
  // Onboarding для новых пользователей
  const { shouldShowOnboarding, hideOnboarding } = useStudioOnboarding();
  
  const handleMenuStateChange = (isOpen: boolean) => {
    setIsMenuOpen(isOpen);
  };

  const features: Feature[] = [
    {
      id: 'knowledge',
      title: 'База знаний',
      icon: <BookOpen className="w-6 h-6" />,
      description: 'Ваша персональная библиотека статей, конспектов и целей',
      path: '/knowledge/root',
      isPrimary: true
    },
    {
      id: 'content-plan',
      title: 'Контент-план',
      icon: <Calendar className="w-8 h-8" />,
      description: 'Ваш персональный план контента с учетом целей и аудитории',
      path: `/articles/content-plan/new-${Date.now()}`
    },
    {
      id: 'target-audience',
      title: 'Целевая аудитория',
      icon: <Users className="w-6 h-6" />,
      description: 'Анализ и сегментация целевой аудитории для вашего контента',
      path: `/audience`
    },
    {
      id: 'reels',
      title: 'Анализ Reels',
      icon: <Instagram className="w-8 h-8" />,
      description: 'Получите детальный анализ залетевших Reels',
      path: `/reels`
    },
    {
      id: 'roadmap',
      title: 'Roadmap',
      icon: <Target className="w-6 h-6" />,
      description: 'Составьте пошаговый план достижения ваших целей',
      path: `/roadmap`
    },
    {
      id: 'notes',
      title: 'Конспект',
      icon: <BookOpen className="w-6 h-6" />,
      description: 'Создайте структурированный конспект из текста, видео или других источников',
      path: `/articles/notes/new-${Date.now()}`
    }
  ];

  return (
    <>
      {/* Onboarding для новых пользователей */}
      <StudioOnboarding 
        isOpen={shouldShowOnboarding} 
        onClose={hideOnboarding} 
      />
      
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 dark:from-gray-900 dark:via-slate-800 dark:to-gray-900 transition-colors duration-200 mobile-content-padding">
        <div className="hidden md:block">
          <NavigationMenu onMenuStateChange={handleMenuStateChange} isMenuActuallyOpen={isMenuOpen} />
        </div>
      
      <div className="max-w-7xl mx-auto px-4 py-8 lg:py-12 w-full">
        <PageHeader
          title="Dissai.io"
          subtitle="Контент маркетинг с искусственным интеллектом"
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pb-8 md:pb-6 w-full p-2">
          {/* Карточка "Моя база знаний" - только для десктопа */}
          {features.filter(f => f.isPrimary).map((feature) => (
            <button
              key={feature.id}
              onClick={() => navigate(feature.path)}
              className="hidden md:block md:col-span-2 p-8 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 text-left hover:scale-[1.02] group"
            >
              <div className="text-white mb-4">
                {feature.icon}
              </div>
              <h2 className="text-2xl font-semibold text-white mb-2">{feature.title}</h2>
              <p className="text-blue-100 group-hover:text-white transition-colors">{feature.description}</p>
            </button>
          ))}

          {/* Остальные карточки */}
          {features.filter(f => !f.isPrimary && f.id !== 'regular-article' && f.id !== 'roadmap' && f.id !== 'notes' && f.id !== 'target-audience').map((feature) => (
            <button
              key={feature.id}
              onClick={() => !feature.inDevelopment && navigate(feature.path)}
              disabled={feature.inDevelopment}
              className={`group relative p-4 md:p-8 rounded-2xl transition-all duration-300 text-left h-28 md:min-h-[200px] w-full max-w-full overflow-hidden ${
                feature.inDevelopment ? 'opacity-60 cursor-not-allowed' : 'hover:scale-[1.02] hover:shadow-2xl'
              } ${
                feature.inDevelopment 
                  ? 'bg-gray-100 dark:bg-gray-700/70' 
                  : feature.id === 'content-plan' || feature.id === 'reels'
                    ? 'bg-gradient-to-r from-blue-600 to-indigo-600 md:bg-gradient-to-br md:from-blue-50 md:to-indigo-50 md:dark:from-gray-800 md:dark:to-gray-800 md:border-2 md:border-blue-200 md:dark:border-blue-800'
                    : 'bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700'
              }`}
            >
              {/* Декоративные элементы для десктопа */}
              <div className="hidden md:block absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-white/20 to-transparent rounded-bl-full opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              
              <div className="relative h-full flex flex-col">
                {feature.inDevelopment && (
                  <div className="absolute top-0 right-0 flex items-center text-yellow-600 dark:text-yellow-400">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    <span className="text-sm">В разработке</span>
                  </div>
                )}
                
                {/* Мобильная версия */}
                <div className="md:hidden flex flex-col h-full w-full overflow-hidden">
                  <div className={`flex items-center mb-2 w-full min-w-0 ${
                    feature.id === 'content-plan' || feature.id === 'reels'
                      ? 'text-white' 
                      : 'text-blue-600 dark:text-blue-400'
                  }`}>
                    <div className="mr-3 flex-shrink-0">
                      {feature.icon}
                    </div>
                    <h2 className={`text-base font-semibold truncate flex-1 min-w-0 ${
                      feature.id === 'content-plan' || feature.id === 'reels'
                        ? 'text-white' 
                        : 'text-gray-900 dark:text-white'
                    }`}>{feature.title}</h2>
                  </div>
                  <p className={`text-sm flex-1 overflow-hidden ${
                    feature.id === 'content-plan' || feature.id === 'reels'
                      ? 'text-white' 
                      : 'text-gray-600 dark:text-gray-300'
                  }`}>{feature.description}</p>
                </div>
                
                {/* Десктопная версия */}
                <div className="hidden md:flex md:flex-col md:h-full md:justify-center">
                  {/* Иконка */}
                  <div className="mb-3">
                    <div className={`p-3 rounded-xl transition-all duration-300 inline-block ${
                      feature.id === 'content-plan' || feature.id === 'reels'
                        ? 'bg-blue-600 text-white group-hover:bg-blue-700 group-hover:scale-110'
                        : 'bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-400'
                    }`}>
                      {feature.icon}
                    </div>
                  </div>
                  
                  {/* Заголовок */}
                  <h2 className={`text-xl font-bold mb-2 transition-colors ${
                    feature.id === 'content-plan' || feature.id === 'reels'
                      ? 'text-gray-900 dark:text-white group-hover:text-blue-700 dark:group-hover:text-blue-400'
                      : 'text-gray-900 dark:text-white'
                  }`}>
                    {feature.title}
                  </h2>
                  
                  {/* Описание */}
                  <p className={`text-sm flex-1 ${
                    feature.id === 'content-plan'
                      ? 'text-gray-700 dark:text-gray-300'
                      : feature.id === 'reels'
                      ? 'text-gray-700 dark:text-gray-300'
                      : 'text-gray-600 dark:text-gray-300'
                  }`}>
                    {feature.description}
                  </p>
                </div>
              </div>
            </button>
          ))}
        </div>
        
      </div>
    </div>
    </>
  );
}

export default Home;
